import { Component, Input } from "@angular/core";
import { State } from "@progress/kendo-data-query";
import { FormGroup, Validators, FormControl, AbstractControl, ValidationErrors } from "@angular/forms";
import { Environment } from "../models/environment";
import { EnvironmentService } from "../models/environment-service";
import { EnvironmentServiceDataService } from "../service/environment-service-data.service";
import { ServiceDataService } from "../service/service-data.service";
import { TenantEnvironment } from "../models/tenant";
import { tap, map, catchError } from "rxjs/operators";
import { DialogRef, DialogService } from "@progress/kendo-angular-dialog";
import { Observable, of } from 'rxjs';
import { EnvironmentServiceConfigurationDataService } from '../service/service-configuration-keys-data.service';
import { InlineEditGridComponent } from 'src/app/core/inline-edit-grid.component';

@Component({
  selector: "app-environment-services",
  templateUrl: "./environment-services.component.html",
  styleUrls: ["./environment-services.component.scss"],
})
export class EnvironmentServicesComponent extends InlineEditGridComponent {
  @Input() public environment: Environment;
  @Input() public tenantEnvironment: TenantEnvironment;

  public readonly: boolean;
  private environmentId: number;
  public serviceMap: Map<number, string> = new Map();
  public services: any[];
  public gridState: State = {
    sort: [],
    skip: 0,
    take: 10,
  };

  constructor(
    private dataService: EnvironmentServiceDataService,
    private serviceDataService: ServiceDataService,
    private envServiceConfigDataService : EnvironmentServiceConfigurationDataService,
    private dialogService: DialogService
  ) {
    super();
  }

  ngOnInit() {
    this.environmentId =
      this.environment?.Id ?? this.tenantEnvironment.EnvironmentId;
    this.readonly = this.tenantEnvironment !== undefined;

    this.serviceDataService.get({}, "").subscribe((result) => {
      this.services = result.data;
      for (const service of this.services) {
        this.serviceMap[service.Id] = service.Name;
      }
      /*this.serviceMap = new Map(
        this.services.map(x => [x.Id, x.Name] as [number, string])
      );*/
    });
    super.ngOnInit();
  }

  protected refresh() {
    this.view = this.dataService
      .get(this.gridState, `&$filter=environmentId eq ${this.environmentId}`)
      .pipe(tap(() => (this.loading = false)));
  }

  public addHandler({ sender }) {
    this.closeEditor(sender);

    this.formGroup = new FormGroup({
      Id: new FormControl(0),
      EnvironmentId: new FormControl(this.environmentId),
      ServiceId: this.CreateServiceIdControl(null)
    });
    sender.addRow(this.formGroup);
  }

  public editHandler({ sender, rowIndex, dataItem }) {
    this.closeEditor(sender);

    this.formGroup = new FormGroup({
      Id: new FormControl(dataItem.Id),
      EnvironmentId: new FormControl(dataItem.EnvironmentId),
      ServiceId: this.CreateServiceIdControl(dataItem.ServiceId)
    });
    this.editedRowIndex = rowIndex;
    sender.editRow(rowIndex, this.formGroup);
  }

  public saveHandler({ sender, rowIndex, formGroup, isNew }) {
    const entity: EnvironmentService = formGroup.value;
    this.loading = true;
    this.refreshObservable(this.dataService.update(entity, isNew));
    sender.closeRow(rowIndex);
  }

  public removeHandler({ dataItem }) {
    const deleteAction = { text: "Remove", primary: true };
    const cancelAction = { text: "Cancel" };

    this.envServiceConfigDataService.overrideCountByEnvironmentService(+dataItem.Id)
    .subscribe(ec => 
      {
        if (+ec['@odata.count'] > 0) {
          let phrase = 'service key override';
          if (+ec['@odata.count'] > 1) {
            phrase = phrase + 's';
          }
          this.dialogService.open({
            title: "Cannot Delete",
            content: `This environment-service has ${ec['@odata.count']} ${phrase}`,
            actions: [cancelAction],
          });
          return;
        }

        const dialog: DialogRef = this.dialogService.open({
          title: "Please confirm",
          content: "This will remove the service from this environment. The service can be added back at a later time; however, all overrides will be lost.",
          actions: [cancelAction, deleteAction],
        });
    
        dialog.result.subscribe((result) => {
          if (result === deleteAction) {
            this.refreshObservable(this.dataService.delete(dataItem.Id));
          }
        });
      });
  }

  private CreateServiceIdControl(serviceId){
    return new FormControl(serviceId ?? 1, {validators: [Validators.required], asyncValidators: [this.UniqueService.bind(this)]});
  }

  UniqueService(ctl: AbstractControl) : Observable<ValidationErrors | null> {
    if (ctl.pristine)
      return of(null);

    let environmentIdCtl = ctl.parent.controls['EnvironmentId'];

    return this.dataService.linkExists(+environmentIdCtl.value, +ctl.value)
    .pipe(
      map(
        r => 
        r === true ? { unique: true } : null),
      catchError(() => of(null))
    );
  }   
}
